<script setup>
import { computed, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import Switch from 'dashboard/components-next/switch/Switch.vue';
import {
  Field,
  FieldGroup,
  FieldLabel,
} from 'dashboard/components-next/polaris/ui/field';
import { Input } from 'dashboard/components-next/polaris/ui/input';

// Rebuilt inside on the kit's PolicyBaseInfo (phase 9.8): name and description as the kit's fields, and the
// status switch the woot has. Same models and `validationChange` event.

defineProps({
  nameLabel: {
    type: String,
    default: '',
  },
  namePlaceholder: {
    type: String,
    default: '',
  },
  descriptionLabel: {
    type: String,
    default: '',
  },
  descriptionPlaceholder: {
    type: String,
    default: '',
  },
  statusLabel: {
    type: String,
    default: '',
  },
  statusPlaceholder: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['validationChange']);

const policyName = defineModel('policyName', {
  type: String,
  default: '',
});

const description = defineModel('description', {
  type: String,
  default: '',
});

const enabled = defineModel('enabled', {
  type: Boolean,
  default: true,
});

const validationRules = {
  policyName: { required, minLength: minLength(1) },
  description: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, { policyName, description });

const isValid = computed(() => !v$.value.$invalid);

watch(
  isValid,
  () => {
    emit('validationChange', {
      isValid: isValid.value,
      section: 'baseInfo',
    });
  },
  { immediate: true }
);
</script>

<template>
  <FieldGroup class="max-w-xl gap-4 pb-4" data-base-info>
    <Field>
      <FieldLabel for="policy-name">{{ nameLabel }}</FieldLabel>
      <Input
        id="policy-name"
        v-model="policyName"
        :placeholder="namePlaceholder"
      />
    </Field>
    <Field>
      <FieldLabel for="policy-description">{{ descriptionLabel }}</FieldLabel>
      <Input
        id="policy-description"
        v-model="description"
        :placeholder="descriptionPlaceholder"
      />
    </Field>
    <Field v-if="statusLabel" orientation="horizontal">
      <Switch id="policy-enabled" v-model="enabled" />
      <FieldLabel for="policy-enabled" class="font-normal">
        <span class="type-polaris-label">{{ statusLabel }}</span>
        <span class="type-polaris-body text-polaris-muted-foreground">
          {{ statusPlaceholder }}
        </span>
      </FieldLabel>
    </Field>
  </FieldGroup>
</template>
